import { Injectable, OnDestroy } from '@angular/core';
import { CommunicationManagerService } from './communication-manager.service';
import { Observable, of, switchMap, tap, map, catchError, BehaviorSubject, Subscription } from 'rxjs';
import { CompanyRole, User } from '../models/user.model';
import { UserEditSave } from '../models/profile-settings.model';
import { LocalStorageService } from './storage.service';
import { CompanyService } from './company.service';

@Injectable({
  providedIn: 'root',
})
export class UsersService implements OnDestroy {
  private usersCache = new Map<string, User[]>();
  private currentCompanyId: string | null = null;
  private companySubscription: Subscription;

  constructor(
    private cm: CommunicationManagerService,
    private localStorageService: LocalStorageService,
    private companyService: CompanyService
  ) {
    // Subscribe to company changes to clear cache when company changes
    this.companySubscription = this.companyService.selectedCompany$.subscribe(company => {
      if (company?._id && company._id !== this.currentCompanyId) {
        this.currentCompanyId = company._id;
        this.clearUsersCache();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.companySubscription) {
      this.companySubscription.unsubscribe();
    }
  }

  private clearUsersCache(): void {
    this.usersCache.clear();
  }

  /**
   * Manually clear the users cache
   */
  public clearCache(): void {
    this.clearUsersCache();
  }

  /**
   * Clear cache for a specific company
   */
  public clearCacheForCompany(companyId: string): void {
    this.usersCache.delete(companyId);
  }

  getLoggedInUserDetails() {
    return this.cm.get(`user`) as Observable<User>;
  }

  getUserById(userId: string) {
    const localUsersEditedBy =
      this.localStorageService.getUserEditedById(userId);
    if (localUsersEditedBy) {
      return of(localUsersEditedBy.fullName);
    } else {
      return this.cm.get(`user/one/${userId}`).pipe(
        tap((user: any) => {
          const userFullName =
            (user?.firstName || '') + ' ' + (user?.lastName || '');
          this.localStorageService.setUserEditedById(user._id, userFullName);
        }),
        switchMap((user) => {
          const userFullName =
            (user?.firstName || '') + ' ' + (user?.lastName || '');

          return of(userFullName);
        })
      );
    }
  }

  getUsersForCompany(companyId: string) {
    // Check if we have cached users for this company
    if (this.usersCache.has(companyId)) {
      return of(this.usersCache.get(companyId)!);
    }

    // Fetch users from API and cache the result
    return this.cm.get(`user/all/company`, [
      { headerName: 'companyId', headerValue: companyId },
    ]).pipe(
      tap((users: User[]) => {
        // Cache the users for this company
        this.usersCache.set(companyId, users);
      }),
      catchError((error) => {
        console.error('Error fetching users for company:', error);
        return of([]);
      })
    ) as Observable<User[]>;
  }

  updateUser(user: UserEditSave) {
    return this.cm.put(`user`, user).pipe(
      tap(() => {
        // Clear cache when user is updated as it might affect user lists
        this.clearUsersCache();
      })
    );
  }

  uploadAvatar(file: File) {
    const payload: FormData = new FormData();
    payload.append('profile', file);

    return this.cm.uploadFileAndGetJson({
      route: `user/upload/profile`,
      payload: payload,
    });
  }

  createUser(email: string, companyRole: CompanyRole) {
    const payload = {
      email,
      // username: email,
      role: companyRole.role,
    };
    return this.cm.post(`user/invite`, payload, [
      { headerName: 'companyId', headerValue: companyRole.company },
    ]).pipe(
      tap(() => {
        // Clear cache for the specific company when a new user is created
        this.clearCacheForCompany(companyRole.company);
      })
    );
  }

  verifyUserAndUpdatePassword(password: string, verificationUrl: string) {
    const payload = {
      password,
    };
    return this.cm.post(verificationUrl, payload);
  }

  setNewUserPassword(password: string, verificationUrl: string) {
    const payload = {
      newPassword: password,
    };
    return this.cm.post(verificationUrl, payload);
  }

  forgotPassword(email: string) {
    const payload = {
      email,
    };
    return this.cm.post('user/forgotPassword', payload);
  }

  isValidToken(token: string) {
    return this.cm.getAndExpectText(`user/isValidToken/${token}`).pipe(
      map((resp: any) => {
        // Return true if status is 200, false for any other status (including 500)
        return resp.status === 200;
      })
    );
  }

  deleteUserFromCompany(userId: string, companyId: string) {
    return this.cm.deleteMultiple(
      `user/role`,
      {
        id: userId,
      },
      [{ headerName: 'companyId', headerValue: companyId }]
    ).pipe(
      tap(() => {
        // Clear cache for the specific company when a user is removed
        this.clearCacheForCompany(companyId);
      })
    );
  }

  addOrUpdateUserCompanyRole(userId: string, companyRole: CompanyRole) {
    return this.cm.put(
      `user/role`,
      {
        userId,
        role: companyRole.role,
      },
      [{ headerName: 'companyId', headerValue: companyRole.company }]
    ).pipe(
      tap(() => {
        // Clear cache for the specific company when user role is updated
        this.clearCacheForCompany(companyRole.company);
      })
    );
  }

  changePassword(
    oldPassword: string,
    newPassword: string,
    confirmedPassword: string
  ) {
    return this.cm.post(`user/changePassword`, {
      oldPassword: oldPassword,
      newPassword: newPassword,
      confirmedPassword: confirmedPassword,
    });
  }
}
